import { Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { useCreateFeatureFlagForm } from '../hooks/use-create-feature-flag-form'

export function CreateFeatureFlagForm({
  onCreated,
  onCancel,
}: {
  onCreated: (key: string) => void
  onCancel: () => void
}) {
  const form = useCreateFeatureFlagForm(onCreated)
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !form.mutation.isPending) {
          onCancel()
        }
      }}
    >
      <DialogContent
        className="ff-console ff-create-dialog"
        showCloseButton={!form.mutation.isPending}
        onPaste={(event) => {
          if (form.handlePaste(event.clipboardData.getData('text/plain'))) {
            event.preventDefault()
          }
        }}
      >
        <section className="ff-create">
          <header className="ff-detail-header">
            <div className="ff-title-row">
              <DialogTitle>Create feature flag</DialogTitle>
            </div>
            <DialogDescription className="ff-intro">
              Enter the details or paste a copied flag to fill them
              automatically.
            </DialogDescription>
          </header>
          <form
            className="ff-editor-panel"
            onSubmit={(event) => {
              event.preventDefault()
              form.submit()
            }}
          >
            <fieldset
              className="ff-create-fields"
              disabled={form.mutation.isPending}
            >
              {form.pasteError ? (
                <p className="ff-form-error" role="alert">
                  {form.pasteError}
                </p>
              ) : form.pasted ? (
                <p role="status">Flag details pasted.</p>
              ) : null}
              <div>
                <Label htmlFor="new-flag-name">Name</Label>
                <Input
                  id="new-flag-name"
                  required
                  placeholder="New job view"
                  value={form.name}
                  onChange={(event) => form.setName(event.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="new-flag-key">Key</Label>
                <Input
                  id="new-flag-key"
                  required
                  pattern="[A-Z][A-Z0-9_]*"
                  aria-describedby="new-flag-key-help"
                  placeholder="NEW_JOB_VIEW"
                  value={form.key}
                  onChange={(event) => form.setKey(event.target.value)}
                />
                <p id="new-flag-key-help">
                  Use uppercase letters, numbers, and underscores. Start with a
                  letter. The key cannot be changed later.
                </p>
              </div>
              <div>
                <Label htmlFor="new-flag-description">
                  Description <span className="ff-optional">Optional</span>
                </Label>
                <Textarea
                  id="new-flag-description"
                  placeholder="What does this flag control?"
                  value={form.description}
                  onChange={(event) => form.setDescription(event.target.value)}
                />
              </div>
              <div className="ff-create-note">
                <Info size={16} />
                <p>
                  New flags start disabled. A developer must connect the key to
                  a feature before it controls access.
                </p>
              </div>
              {form.errorMessage ? (
                <p className="ff-form-error" role="alert">
                  {form.errorMessage}
                </p>
              ) : null}
            </fieldset>
            <footer className="ff-savebar">
              <span>Disabled by default</span>
              <div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onCancel}
                  disabled={form.mutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  className="ff-save-button"
                  disabled={
                    form.mutation.isPending || !form.name.trim() || !form.key
                  }
                >
                  {form.mutation.isPending ? 'Creating…' : 'Create flag'}
                </Button>
              </div>
            </footer>
          </form>
        </section>
      </DialogContent>
    </Dialog>
  )
}
